import { tr, usePartnerTranslation } from "./lib/language";
import React, { useEffect } from "react";
import "@/App.css";
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import DummyPage from "./pages/DummyPage";
import RefundRequestsPage from "./pages/portal/RefundRequestsPage";
import BookingsPage from "./pages/portal/BookingsPage";
import EditShopPage from "./pages/portal/EditShopPage";
import UploadGalleryPage from "./pages/portal/UploadGalleryPage";
import FinancePage from "./pages/portal/FinancePage";
import HomePage from "./pages/portal/HomePage";
import ProductsPage from "./pages/portal/ProductsPage";
import SupportTicketsPage from "./pages/portal/SupportTicketsPage";
import CreateProductPage from "./pages/portal/CreateProductPage";
import EditProductPage from "./pages/portal/EditProductPage";
import AccountManagementPage from "./pages/portal/AccountManagementPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import ChangePasswordPage from "./pages/portal/ChangePasswordPage";
import LegalPage from "./pages/portal/LegalPage";
import StreamPage from "./pages/portal/StreamPage";
import SupplierFaqPage from "./pages/SupplierFaqPage";
import ContactPage from "./pages/ContactPage";
import { useAuth, defaultPageFor, normalizeRole } from "./lib/auth";
import { startSupplierSessionMonitor } from "./lib/sessionRefresh";
import { LanguageProvider, useLanguage } from "./lib/language";

const ALL_PORTAL_ROLES = ["administration", "operations", "finance"];

function Layout({ children }) {
  usePartnerTranslation();
  const { language, setLanguage, t } = useLanguage();
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header language={language} setLanguage={setLanguage} t={t} />
      {children}
      <Footer />
    </div>
  );
}

function RequireAuth({ children, allowedRoles }) {
  usePartnerTranslation();
  const { user, role } = useAuth();
  const location = useLocation();
  const sessionUserId = user?.user_id || user?.userId || user?.email;
  useEffect(() => {
    if (!sessionUserId) return undefined;
    return startSupplierSessionMonitor();
  }, [sessionUserId]);
  if (!user) {
    return <Navigate to="/" replace state={{ from: location.pathname + location.search }} />;
  }
  // Owner (is_owner flag) is superuser — unrestricted access to all routes
  if (allowedRoles && allowedRoles.length > 0 && !user.is_owner) {
    const normalizedRole = normalizeRole(role);
    const normalizedAllowedRoles = allowedRoles.map(normalizeRole);
    if (!normalizedRole || !normalizedAllowedRoles.includes(normalizedRole)) {
      const target = ALL_PORTAL_ROLES.includes(normalizedRole) ? defaultPageFor(normalizedRole) : "/support";
      return <Navigate to={target} replace />;
    }
  }
  return children;
}

function RedirectIfAuthenticated({ children }) {
  usePartnerTranslation();
  const { user, role } = useAuth();
  const location = useLocation();
  if (user) {
    return <Navigate to={/^\/refund-requests(?:\?|$)/.test(location.state?.from || "") ? location.state.from : defaultPageFor(role)} replace />;
  }
  return children;
}

const PORTAL_ROUTES = [
  { path: "/my-shop/view", get title() { return tr("View My Shop"); }, allowedRoles: ALL_PORTAL_ROLES },
  { path: "/my-shop/add-product", get title() { return tr("Add a Product"); }, allowedRoles: ["administration", "operations"] },
];

const PUBLIC_DUMMY_PATHS = [
  "about",
  "blog",
  "safety",
];

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route
        path="/"
        element={
          <RedirectIfAuthenticated>
            <Layout>
              <LoginPage />
            </Layout>
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/signup"
        element={
          <RedirectIfAuthenticated>
            <Layout>
              <SignupPage />
            </Layout>
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/become-partner"
        element={
          <Layout>
            <DummyPage title={tr("Become a Partner")} />
          </Layout>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <Layout>
            <ForgotPasswordPage />
          </Layout>
        }
      />
      <Route
        path="/invite"
        element={
          <Layout>
            <AcceptInvitePage />
          </Layout>
        }
      />
      <Route
        path="/faqs"
        element={
          <Layout>
            <SupplierFaqPage />
          </Layout>
        }
      />
      <Route
        path="/contact"
        element={
          <Layout>
            <ContactPage />
          </Layout>
        }
      />
      <Route
        path="/partner-legal-center"
        element={
          <Layout>
            <LegalPage />
          </Layout>
        }
      />

      {/* Public shop routes now live in the main OTA app. Attempting them here
          redirects to login. */}

      {/* Protected portal routes (require login) */}
      <Route
        path="/home"
        element={
          <RequireAuth allowedRoles={["administration", "operations"]}>
            <Layout>
              <HomePage />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/products"
        element={
          <RequireAuth allowedRoles={["administration", "operations"]}>
            <Layout>
              <ProductsPage />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/create"
        element={
          <RequireAuth allowedRoles={["administration", "operations"]}>
            <Layout>
              <CreateProductPage />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/products/:id/edit"
        element={
          <RequireAuth allowedRoles={["administration", "operations"]}>
            <Layout>
              <EditProductPage />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/account"
        element={
          <RequireAuth allowedRoles={["administration"]}>
            <Layout>
              <AccountManagementPage />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/account/change-password"
        element={
          <RequireAuth>
            <Layout>
              <ChangePasswordPage />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/account/legal"
        element={
          <RequireAuth allowedRoles={["administration"]}>
            <Layout>
              <LegalPage />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/stream"
        element={
          <RequireAuth allowedRoles={["administration", "operations"]}>
            <StreamPage />
          </RequireAuth>
        }
      />
      <Route
        path="/bookings"
        element={
          <RequireAuth allowedRoles={["administration", "operations"]}>
            <Layout>
              <BookingsPage />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/my-shop/edit"
        element={
          <RequireAuth allowedRoles={["administration", "operations"]}>
            <Layout>
              <EditShopPage />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/my-shop/gallery"
        element={
          <RequireAuth allowedRoles={["administration", "operations"]}>
            <Layout>
              <UploadGalleryPage />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/finance/*"
        element={
          <RequireAuth allowedRoles={["administration", "finance"]}>
            <Layout>
              <FinancePage />
            </Layout>
          </RequireAuth>
        }
      />
      <Route path="/refund-requests" element={<RequireAuth><Layout><RefundRequestsPage /></Layout></RequireAuth>} />
      <Route
        path="/support"
        element={
          <RequireAuth>
            <Layout>
              <SupportTicketsPage />
            </Layout>
          </RequireAuth>
        }
      />
      {PORTAL_ROUTES.map((r) => (
        <Route
          key={r.path}
          path={r.path}
          element={
            <RequireAuth allowedRoles={r.allowedRoles}>
              <Layout>
                <DummyPage title={r.title} />
              </Layout>
            </RequireAuth>
          }
        />
      ))}

      {/* Public footer dummy routes */}
      {PUBLIC_DUMMY_PATHS.map((path) => (
        <Route
          key={path}
          path={`/${path}`}
          element={
            <Layout>
              <DummyPage />
            </Layout>
          }
        />
      ))}

      {/* Catch-all → land on login (customer routes now live on the customer app) */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>,
  ),
);

function App() {
  usePartnerTranslation();
  return (
    <div className="App">
      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{ duration: 4000 }}
      />
      <LanguageProvider>
        <RouterProvider router={router} />
      </LanguageProvider>
    </div>
  );
}

export default App;
